import { apiCall } from "./api";

const URL = "/api/v1/manage-dashboard/organization";

export const getOrganizations = () => {
  return apiCall(`${URL}s`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
};

export const createOrganization = (data) => {
  return apiCall(URL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });
};

export const getOrganization = (organizationId) => {
  return apiCall(`${URL}/${organizationId}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
};

export const updateOrganization = (organizationId, data) => {
  return apiCall(`${URL}/${organizationId}`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });
};

export const deleteOrganization = (organizationId) => {
  return apiCall(`${URL}/${organizationId}`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
};

export const getOrganizationWorkspaces = (organizationId) => {
  return apiCall(`${URL}/${organizationId}/workspaces`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
};

export const addWorkspaceToOrganization = (organizationId, workspaceId) => {
  return apiCall(`${URL}/${organizationId}/workspaces`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ workspaceId }),
  });
};

export const removeWorkspaceFromOrganization = (organizationId, workspaceId) => {
  return apiCall(`${URL}/${organizationId}/workspaces/${workspaceId}`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
};

// "shared" = listed in the org's workspace picker, so org members can join it
// as members (it grants no access by itself); "private" = not listed, invite-only.
// Org admins see every workspace either way. The self-serve equivalent needs a
// workspace admin, so this is the support path for an org without one.
export const setWorkspaceOrgVisibility = (
  organizationId,
  workspaceId,
  orgVisibility,
) => {
  return apiCall(
    `${URL}/${organizationId}/workspaces/${workspaceId}/visibility`,
    {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ orgVisibility }),
    },
  );
};

export const getOrganizationUsers = (organizationId) => {
  return apiCall(`${URL}/${organizationId}/users`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
};

export const removeOrganizationUser = (organizationId, data) => {
  return apiCall(`${URL}/${organizationId}/remove-user`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });
};

export const changeOrganizationUserRole = (organizationId, data) => {
  return apiCall(`${URL}/${organizationId}/change-user-role`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });
};

export const getOrganizationJoinRequests = (organizationId) => {
  return apiCall(`${URL}/${organizationId}/join-requests`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
};

export const approveJoinRequest = (requestId) => {
  return apiCall(`/api/v1/manage-dashboard/join-request/${requestId}/approve`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
};

export const rejectJoinRequest = (requestId) => {
  return apiCall(`/api/v1/manage-dashboard/join-request/${requestId}/reject`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
};

export const getOrganizationInvites = (organizationId) => {
  return apiCall(`${URL}/${organizationId}/invites`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
};

export const cancelOrganizationInvite = (token) => {
  return apiCall(`/api/v1/manage-dashboard/org-invite-cancel/${token}`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
};

// Domain claims. An org can only self-serve the domain matching its own admin's
// email address, so any additional domains it owns are added here — once a
// Streamoid admin has verified the org actually owns them. Users from a claimed
// domain always auto-join as members; the backend forces autoJoinRole "member".
export const getOrganizationDomainInvites = (organizationId) => {
  return apiCall(`${URL}/${organizationId}/domain-invites`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
};

export const addOrganizationDomainInvite = (organizationId, data) => {
  return apiCall(`${URL}/${organizationId}/domain-invite`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });
};

export const revokeOrganizationDomainInvite = (organizationId, domainInviteId) => {
  return apiCall(
    `${URL}/${organizationId}/domain-invite/${domainInviteId}`,
    {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
    },
  );
};

// Workspace → organization migration (group workspaces by owner email domain).
export const getWorkspaceOrgMigrationPlan = () => {
  return apiCall(`/api/v1/manage-dashboard/workspace-org-migration/plan`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
};

export const migrateWorkspacesByDomain = (data) => {
  return apiCall(`/api/v1/manage-dashboard/workspace-org-migration/migrate`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });
};
